/**
 * SubtaskNotePanel — collapsible single-text-field note shared across the
 * whole subtask (NOT per-client). Used on Audit boards (Statutory / Tax /
 * Trust) where the team often needs a scratch-pad of context, scoping notes,
 * or auditor remarks that apply uniformly to the engagement.
 *
 * Saves on Save click (no autosave — admins want to edit freely without
 * triggering toasts on every keystroke). Empty string is valid (clears).
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronUp, MessageSquare, Save, X } from 'lucide-react';
import { tasksAPI } from '@/lib/api';
import { toast } from 'sonner';

export default function SubtaskNotePanel({ task, subtask, canManage, onReload }) {
  const initial = subtask?.subtask_note || '';
  const [open, setOpen] = useState(!!initial);
  const [draft, setDraft] = useState(initial);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(subtask?.subtask_note || '');
    setEditing(false);
  }, [subtask?.subtask_note, subtask?.id]);

  const dirty = draft !== (subtask?.subtask_note || '');

  const handleSave = async () => {
    setSaving(true);
    try {
      await tasksAPI.updateSubtask(task.id, subtask.id, { subtask_note: draft });
      toast.success('Subtask note saved');
      setEditing(false);
      onReload?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save note');
    }
    setSaving(false);
  };

  const handleCancel = () => {
    setDraft(subtask?.subtask_note || '');
    setEditing(false);
  };

  return (
    <Card data-testid="subtask-note-panel">
      <CardContent className="p-0">
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="w-full flex items-center gap-3 px-5 py-3 hover:bg-muted/30 transition-colors"
          data-testid="subtask-note-toggle"
        >
          <MessageSquare className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-semibold">Subtask Note</span>
          {initial ? (
            <Badge variant="secondary" className="text-[10px]">Saved</Badge>
          ) : (
            <Badge variant="outline" className="text-[10px] text-muted-foreground">Empty</Badge>
          )}
          <span className="ml-auto">
            {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
          </span>
        </button>
        {open && (
          <div className="px-5 pb-4 pt-1 space-y-2 border-t">
            <p className="text-xs text-muted-foreground">
              Shared across all clients on this subtask. Use it for engagement-level notes (scoping, auditor remarks, sample size, etc.) — not for per-client comments.
            </p>
            {editing || canManage === false ? (
              <Textarea
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onFocus={() => canManage && setEditing(true)}
                placeholder="Add a note for the whole subtask..."
                className="text-sm min-h-[110px]"
                disabled={!canManage}
                data-testid="subtask-note-textarea"
              />
            ) : (
              <button
                type="button"
                onClick={() => canManage && setEditing(true)}
                className="w-full text-left bg-muted/20 rounded-md p-3 text-sm whitespace-pre-wrap min-h-[60px] hover:bg-muted/30 transition-colors"
                data-testid="subtask-note-display"
              >
                {initial || <span className="text-muted-foreground italic">Click to add a subtask-level note...</span>}
              </button>
            )}
            {canManage && editing && (
              <div className="flex items-center justify-end gap-2">
                <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={handleCancel} disabled={saving}>
                  <X className="h-3 w-3" /> Cancel
                </Button>
                <Button size="sm" className="h-7 text-xs gap-1" onClick={handleSave} disabled={saving || !dirty} data-testid="subtask-note-save">
                  <Save className="h-3 w-3" /> {saving ? 'Saving...' : 'Save note'}
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
